import { Switch, SwitchLabel, TextField, Grid } from '@barracuda-internal/bds-core-v2';
import { makeStyles } from '@material-ui/core/styles';
import TextFieldsIcon from '@material-ui/icons/TextFields';
import { Alert } from '@material-ui/lab';
import React, { useState } from 'react';
import FullPageError from 'src/components/FullPageError';
import { useAppToolchain } from 'src/hooks/useAppToolchain';
import useAPIEffect from 'src/lib/useAPIEffect';
import { ClassifierWordsView } from 'src/types/dataTypes';
import ulog from 'ulog';
import EditViewDialog, { EditViewDialogProps } from './EditViewDialog';
import { validate, validateWords } from 'src/components/Validation';
import useSettingsStyles from '../styles/settings';
const useStyles = makeStyles((theme) => ({
	alert: {
		marginBottom: theme.spacing(2),
	},
	row: {
		borderBottom: 'thin solid lightgray',
		padding: theme.spacing(2),
	},
	optionCell: {
		display: 'flex',
		justifyContent: 'center',
		alignItems: 'flex-start',
		paddingTop: '2px',
	},
}));


interface Props extends EditViewDialogProps {
	accountId: string;
	onSave?: () => {};
}

function EditWordViewDialog({ baseClassifier, onClose, onSave }: Props) {
	const { api } = useAppToolchain();
	const classes = useStyles();
	const customClasses = useSettingsStyles();
	const [isLoading, setIsLoading] = useState(false);
	const [formState, setFormState] = useState({
		words: '',
		caseSensitive: false,
		displayName: '',
		matchWholeWord: true,
		tagName: '',
		disabled: true,
	});
	const [doSave, setDoSave] = useState(false);
	const [isSaving, setIsSaving] = useState(false);

	const [uiError, setUIError] = useState({
		words: { error: false, msg: '' },
		displayName: { error: false, msg: '' },
		tagName: { error: false, msg: '' },
	  });

	const [classifier, setClassifier]: [
		ClassifierWordsView,
		React.Dispatch<ClassifierWordsView>,
	] = useState();

	const [loadError, setLoadError]: [Error, React.Dispatch<Error>] =
		useState();
	const [saveError, setSaveError] = useState<string>('');
	const defaultSaveErrorMessage = 'Error saving form! Please wait a few moments and try again.';

	const extractErrorMessage = (detail: string): string => {
		const firstLine = detail.split('\n')[0];
		const exceptionMsgMatch = firstLine.match(/Exception:\s*(.+)/);
		return exceptionMsgMatch ? exceptionMsgMatch[1].trim() : firstLine.trim();
	};

	const handleSaveError = async (err) => {
		ulog.error(err);
		try {
			if (err?.response?.json) {
				const errorData = await err.response.json();
				const detail = errorData?.Detail;
				setSaveError(detail ? extractErrorMessage(detail) : defaultSaveErrorMessage);
			} else {
				setSaveError(err?.message || defaultSaveErrorMessage);
			}
		} catch {
			setSaveError(defaultSaveErrorMessage);
		}
	};

	useAPIEffect((cancelled) => {
		setClassifier(baseClassifier as ClassifierWordsView);
		if (baseClassifier.id != '') {
			api.get<{ data: ClassifierWordsView }>(
				baseClassifier.configUrl,
				{},
				{},
				false,
			)
				.then((res) => {
					if (cancelled) return;

					setClassifier(res.data);
					setFormState({
						words: res?.data.words.join('\n').trim(),
						caseSensitive: res?.data.caseSensitive,
						matchWholeWord: res?.data.matchWholeWord,
						displayName: res?.data.displayName || '',
						tagName: res?.data.tagName || '',
						disabled: res?.data.disabled,
					});
				})
				.catch((err) => {
					ulog.error(err);
					setLoadError(err);
				})
				.finally(() => {
					if (cancelled) return;
					setIsLoading(false);
				});
		}
	}, []);
	function hasUIValidations(): boolean {
		const { displayName, tagName, words } = formState;
		const displayNameError = validate({ value: displayName });
		const tagNameError = validate({ value: tagName });
		const wordsError = validateWords({ words });

		const newUIError = {
			displayName: { error: Boolean(displayNameError), msg: displayNameError },
			tagName: { error: Boolean(tagNameError), msg: tagNameError },
			words: { error: Boolean(wordsError), msg: wordsError },
		};

		setUIError(newUIError);
		return Object.values(newUIError).some((field: any) => field.error);
	}
	useAPIEffect(
		(cancelled) => {
			if (!doSave) return;
			if (hasUIValidations()) {
				setDoSave(false);
				return;
			}

			const patchObj = [
				{
					path: '/displayName',
					op: 'replace',
					value: formState.displayName,
				},
				{
					path: '/words',
					op: 'replace',
					value: formState.words.split('\n'),
				},
				{
					path: '/matchWholeWord',
					op: 'replace',
					value: formState.matchWholeWord,
				},
				{
					path: '/caseSensitive',
					op: 'replace',
					value: formState.caseSensitive,
				},
				{
					path: '/disabled',
					op: 'replace',
					value: formState.disabled,
				},
				{
					path: '/tagName',
					op: 'replace',
					value: formState.tagName,
				},
			];

			setIsSaving(true);
			const save = (id) => {
				if (cancelled) return;
				api.updateWordViewClassifier(classifier.accountId, id, patchObj)
					.then((res1) => {
						if (cancelled) return;
						cancelled = true;
						onSave && onSave();
						onClose && onClose();
					})
					.catch((err) => {
						if (cancelled) return;
						handleSaveError(err);
					})
					.finally(() => {
						if (cancelled) return;
						setDoSave(false);
						setIsSaving(false);
					});
			};
			if (baseClassifier.id == '') {
				api.createClassifier(baseClassifier.accountId, {
					type: baseClassifier.type,
					disabled: formState.disabled,
					displayName: formState.displayName,
					tagName: formState.tagName,
					caseSensitive: formState.caseSensitive,
					matchWholeWord: formState.matchWholeWord,
					words: formState.words.split('\n'),
				})
					.then(() => {
						if (cancelled) return;
						cancelled = true;
						onSave && onSave();
						onClose && onClose();
					})
					.catch((err) => {
						if (cancelled) return;
						handleSaveError(err);
					})
					.finally(() => {
						if (cancelled) return;
						setDoSave(false);
						setIsSaving(false);
					});
			} else {
				save(baseClassifier.id);
			}
		},
		[doSave],
	);


	const handleTextChange = (e) => {
		setFormState({ ...formState, words: e.target.value });
		const error = validateWords({ words: e.target.value });
		setUIError((prevState) => ({
			...prevState,
			words: { error: Boolean(error), msg: error },
		  }));
	};

	const handleDisplayNameChange = (e) => {
		setFormState({ ...formState, displayName: e.target.value });
		const error = validate( { value:  e.target.value});
		setUIError((prevState) => ({
			...prevState,
			displayName: { error: Boolean(error), msg: error },
		  }));
	};

	const handleLabelInputChange = (e) => {
		setFormState({ ...formState, tagName: e.target.value });
		const error = validate( { value:  e.target.value});
		setUIError((prevState) => ({
			...prevState,
			tagName: { error: Boolean(error), msg: error },
		  }));
	};

	if (!!loadError)
		return <FullPageError message="An error occured while loading." />;

	return (
		<EditViewDialog
			baseClassifier={baseClassifier}
			Icon={TextFieldsIcon}
			isLoading={isLoading}
			isSaving={isSaving}
			onClose={onClose}
			title="Keyword List"
			onChange={(newFormState) => {
				ulog.debug('Base form state changed', newFormState);
				setFormState({
					...formState,
					...newFormState,
				});
			}}
			onSave={() => setDoSave(true)}
		>
			{!!classifier ? (
				<div>
					{!!saveError ? (
						<Alert className={classes.alert} severity="error">
							{saveError}
						</Alert>
					) : null}
					<TextField
						fullWidth
						label="Classifier Name"
						onChange={handleDisplayNameChange}
						className={customClasses['mb_16']}
						value={formState.displayName}
						variant="outlined"
						required
						inputProps={{ maxLength: 64 }}
						error={uiError.displayName.error}
						helperText = {uiError.displayName.msg}
					/>
					<TextField
						fullWidth
						label="Label Name"
						helperText={ uiError.tagName.error ? uiError.tagName.msg : "This label will display in your feed when matching text is found."}
						margin="normal"
						onChange={handleLabelInputChange}
						value={formState.tagName}
						variant="outlined"
						required
						inputProps={{ maxLength: 64 }}
						error={uiError.tagName.error}
					/>
					<TextField
						aria-label="keywords"
						fullWidth
						helperText={ uiError.words.error ? uiError.words.msg : "Enter keywords and phrases separated by new lines."}
						label="Keywords"
						margin="normal"
						multiline
						onChange={handleTextChange}
						rows={3}
						value={formState.words}
						variant="outlined"
						required
						error={uiError.words.error}
					/>
					<h3 style={{ marginBottom: '8px' }}>Options</h3>
					<Grid container className={classes.row}>
						<Grid item xs={10}>
							<h4 style={{ marginTop: 0 }}>Case Sensitivity</h4>
							<p>
								When this option is enabled, the classifier will
								only look for text that matches the exact letter
								case as the specified keywords or phrases.
							</p>
							<p>For example, "cat" will not match "Cat".</p>
						</Grid>
						<Grid item xs={2} className={classes.optionCell}>
							<SwitchLabel
								sx={{ gap: 1 }}
								label={formState.caseSensitive ? 'On' : 'Off'}
								labelPlacement="start"
								control={
									<Switch
										checked={formState.caseSensitive}
										onChange={(e) =>
											setFormState({
												...formState,
												caseSensitive: e.target.checked,
											})
										}
									/>
								}
							/>
						</Grid>
					</Grid>
					<Grid container className={classes.row}>
						<Grid item xs={10}>
							<h4 style={{ marginTop: 0 }}>Partial Matching</h4>
							<p>
								When this option is enabled, the classifier will
								look for any text that contains the specified
								keywords or phrases and not just exact matches.
							</p>
							<p>
								For example, "cat" will match both "cat" and
								"caterpillar".
							</p>
						</Grid>
						<Grid item xs={2} className={classes.optionCell}>
							<SwitchLabel
								sx={{ gap: 1 }}
								label={!formState.matchWholeWord ? 'On' : 'Off'}
								labelPlacement="start"
								control={
									<Switch
										checked={!formState.matchWholeWord}
										onChange={(e) =>
											setFormState({
												...formState,
												matchWholeWord:
													!e.target.checked,
											})
										}
									/>
								}
							/>
						</Grid>
					</Grid>
				</div>
			) : null}
		</EditViewDialog>
	);
}

export default EditWordViewDialog;
